import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { dropLeadingHeading, topicLabel } from '@zoxo/shared';

import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ShareActions } from '../../components/share-actions';
import { ErrorBoundary } from '../../components/error-boundary';
import { Pencil, Trash2 } from '@/components/icons';
import { useMe } from '../auth/use-me';
import { DELETE_PILL, EDIT_PILL } from '../articles/article-owner-actions';
import { ArticlePaper } from '../articles/article-paper';
import { SurveyBody } from '../articles/prose-body';
import { formatDate } from '../articles/format';
import { CommentList } from '../comments/comment-list';
import { ReactionBar } from '../reactions/reaction-bar';
import { SurveyVote } from './survey-vote';
import { useDeletePost, usePost } from './use-posts';

/** 커뮤니티 글 상세 — 아티클 상세와 같은 부품(본문·반응·공유·댓글). 작성자는 수정, 관리자는 수정 | 삭제. */
export function PostDetail({ id }: { id: string }) {
  const { data: post } = usePost(id);
  const { data: me } = useMe();
  const navigate = useNavigate();
  const del = useDeletePost(id);
  const [confirming, setConfirming] = useState(false);
  const isAdmin = me?.role === 'admin';
  const canEdit = !!post.isMine || isAdmin;

  return (
    <article>
      <h1 className="mb-3 text-2xl font-bold leading-tight tracking-tight text-gray-900">{post.title}</h1>
      <div className="mb-6 flex flex-wrap items-center gap-1.5 border-b border-border pb-3 text-sm text-text-muted">
        {/* 게시판 알약 — 목록과 같은 모양, 글쓴이 왼쪽. 누르면 그 게시판 목록. */}
        {post.topic ? (
          <Link
            to="/posts"
            search={{ topic: post.topic }}
            className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs font-medium text-gray-600 hover:border-gray-400"
          >
            {topicLabel(post.topic)}
          </Link>
        ) : null}
        <span>{post.author.nickname}</span>
        <span aria-hidden="true">·</span>
        <span>{formatDate(post.createdAt)}</span>
        <span aria-hidden="true">·</span>
        <span>조회 {post.viewCount}</span>
      </div>

      {/* 아티클 상세의 「✎ 수정」과 같은 알약·같은 자리(오른쪽 끝). 삭제는 글자 버튼. */}
      {canEdit ? (
        <div className="mb-6 flex items-center justify-end gap-2">
          <Link to="/posts/$id/edit" params={{ id: post.id }} className={EDIT_PILL}>
            <Pencil className="size-3.5" />
            수정
          </Link>
          {isAdmin || post.isMine ? (
            <button type="button" className={DELETE_PILL} onClick={() => setConfirming(true)} disabled={del.isPending}>
              <Trash2 className="size-3.5" />
              삭제
            </button>
          ) : null}
        </div>
      ) : null}

      {/* 아티클 상세와 같은 종이(베이지 바탕·테두리·명조체·글자 크기 버튼). */}
      <ArticlePaper>
        <SurveyBody body={dropLeadingHeading(post.body)} />
        {/* 설문은 본문의 일부 — 같은 종이 안, 본문 아래. */}
        {post.survey ? <SurveyVote postId={post.id} survey={post.survey} /> : null}
      </ArticlePaper>

      <div className="mt-6 flex flex-wrap items-start justify-between gap-3 border-t border-border pt-3">
        <ReactionBar target={{ kind: 'post', id: post.id }} likeCount={post.likeCount} dislikeCount={post.dislikeCount} myReaction={post.myReaction} />
        <ShareActions targetId={post.id} path={`/posts/${post.id}`} title={post.title} description={post.title} />
      </div>

      <ErrorBoundary resetKey={id} fallback={() => <p className="text-sm text-text-muted">댓글을 불러오지 못했어요.</p>}>
        <div className="mt-8">
          <CommentList target={{ kind: 'post', id }} />
        </div>
      </ErrorBoundary>

      {/* 설문 제거·이미지 삭제와 같은 확인창(shadcn). 예를 눌러야 지운다. */}
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          del.mutate(undefined, { onSuccess: () => void navigate({ to: '/posts', search: {} }) });
        }}
        title="정말로 글을 삭제할까요?"
        description="되돌릴 수 없어요."
        confirmLabel="예"
        cancelLabel="아니오"
        destructive
        loading={del.isPending}
      />
    </article>
  );
}
